import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

import { mobileNewsApi } from '../api/mobileNews';

const emptyForm = {
  title: '',
  description: '',
  body: '',
  image_url: '',
  is_published: false,
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return value;
  }
};

const MobileNewsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');

  const loadNews = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await mobileNewsApi.list(100, 0);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || 'Не удалось загрузить новости'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  const handleSelect = (item) => {
    setSelectedId(item.id);
    setForm({
      title: item.title || '',
      description: item.description || '',
      body: item.body || '',
      image_url: item.image_url || '',
      is_published: !!item.is_published,
    });
    setFormError('');
  };

  const handleNew = () => {
    setSelectedId(null);
    setForm(emptyForm);
    setFormError('');
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setFormError('Укажите заголовок новости');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description?.trim() || null,
        body: form.body?.trim() || null,
        image_url: form.image_url?.trim() || null,
        is_published: form.is_published,
      };
      if (selectedId) {
        await mobileNewsApi.update(String(selectedId), payload);
      } else {
        const created = await mobileNewsApi.create(payload);
        setSelectedId(created.id);
      }
      await loadNews();
    } catch (err) {
      setFormError(
        err?.response?.data?.detail || err?.message || 'Не удалось сохранить новость'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      className="home-container page-animate"
      sx={{
        px: { xs: 2, md: 3 },
        py: 3,
        maxWidth: 1400,
        mx: 'auto',
        width: '100%',
      }}
    >
      <Paper
        className="glass-sheen"
        sx={{
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 4,
          mb: 3,
          background: 'var(--surface-bg)',
          border: (theme) =>
            theme.palette.mode === 'dark'
              ? '1px solid rgba(148, 163, 184, 0.2)'
              : '1px solid rgba(15, 23, 42, 0.08)',
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CampaignRoundedIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Новости мобильного приложения
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Создавайте и публикуйте новости для пользователей мобильного приложения.
            Можно добавить изображение, описание и полный текст новости.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              icon={<AutoAwesomeRoundedIcon />}
              label={`Всего новостей: ${total}`}
              variant="outlined"
            />
            <Chip
              label={`Опубликовано: ${items.filter((i) => i.is_published).length}`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`Черновики: ${items.filter((i) => !i.is_published).length}`}
              color="warning"
              variant="outlined"
            />
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.15fr 0.85fr' },
          gap: 2,
        }}
      >
        <Paper sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 4 }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1.5}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedId ? 'Редактирование новости' : 'Новая новость'}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={handleNew}>
                  Новая
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Сохраняем…' : 'Сохранить'}
                </Button>
              </Stack>
            </Stack>

            {formError && <Alert severity="warning">{formError}</Alert>}

            <TextField
              label="Заголовок"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Краткое описание"
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Полный текст новости"
              value={form.body}
              onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))}
              fullWidth
              multiline
              minRows={6}
            />
            <TextField
              label="URL изображения"
              value={form.image_url}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, image_url: e.target.value }))
              }
              fullWidth
              InputProps={{
                startAdornment: <ImageRoundedIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_published}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, is_published: e.target.checked }))
                  }
                />
              }
              label="Опубликовать новость сразу"
            />

            <Divider />

            <Typography variant="subtitle2" color="text.secondary">
              Предпросмотр
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                backgroundColor: 'rgba(148, 163, 184, 0.08)',
              }}
            >
              <Stack spacing={1.5}>
                {form.image_url ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: 180,
                      borderRadius: 2,
                      overflow: 'hidden',
                      backgroundColor: 'rgba(15, 23, 42, 0.08)',
                    }}
                  >
                    <img
                      src={form.image_url}
                      alt="preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </Box>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ImageRoundedIcon color="disabled" />
                    <Typography variant="body2" color="text.secondary">
                      Изображение не задано
                    </Typography>
                  </Stack>
                )}
                <Typography variant="h6">
                  {form.title?.trim() || 'Заголовок новости'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.description?.trim() || 'Краткое описание новости'}
                </Typography>
                <Typography variant="body2">
                  {form.body?.trim() || 'Полный текст новости появится здесь.'}
                </Typography>
              </Stack>
            </Paper>
          </Stack>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 4 }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Список новостей
              </Typography>
              <Button variant="text" onClick={loadNews} disabled={loading}>
                Обновить
              </Button>
            </Stack>
            {loading ? (
              <Stack alignItems="center" sx={{ py: 6 }}>
                <CircularProgress />
              </Stack>
            ) : items.length === 0 ? (
              <Alert severity="info">Новостей пока нет</Alert>
            ) : (
              <Stack spacing={1.5}>
                {items.map((item) => {
                  const isActive = String(item.id) === String(selectedId);
                  return (
                    <Paper
                      key={item.id}
                      variant="outlined"
                      onClick={() => handleSelect(item)}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        cursor: 'pointer',
                        borderColor: isActive ? 'primary.main' : 'divider',
                        backgroundColor: isActive
                          ? 'rgba(37, 99, 235, 0.08)'
                          : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <Stack spacing={1}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={1}
                        >
                          <Typography sx={{ fontWeight: 600 }}>
                            {item.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={item.is_published ? 'Опубликовано' : 'Черновик'}
                            color={item.is_published ? 'success' : 'default'}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {item.description || 'Описание не задано'}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip size="small" label={`Создано: ${formatDate(item.created_at)}`} />
                          <Chip
                            size="small"
                            label={`Обновлено: ${formatDate(item.updated_at)}`}
                          />
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default MobileNewsPage;
